import { useState } from 'react';
import { Tag, X, Save, AlertTriangle, Lock } from 'lucide-react';
import { filesAPI } from '../api';
import './BulkEditor.css';

function GroupBulkEditor({ groupId, groupName, files, onClose }) {
  const groupFiles = files.filter(f => f.group === groupId);
  // Файлы под замком (`locked`) в массовую запись не идут — см. FileEditor
  const activeGroupFiles = groupFiles.filter(f => f.mode !== 'nonactive' && !f.locked);
  const skippedGroupFiles = groupFiles.filter(f => f.mode === 'nonactive' && !f.locked);
  const lockedGroupFiles = groupFiles.filter(f => f.locked);
  const [editText, setEditText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [appendMode, setAppendMode] = useState(false);

  const handleSave = async () => {
    if (!editText.trim()) { setMessage('Введите текст для сохранения'); return; }
    if (activeGroupFiles.length === 0) { setMessage('Нет активных файлов в группе'); return; }
    const skipParts = [];
    if (skippedGroupFiles.length > 0) skipParts.push(`${skippedGroupFiles.length} неактивных`);
    if (lockedGroupFiles.length > 0) skipParts.push(`${lockedGroupFiles.length} под замком`);
    const skipInfo = skipParts.length ? ` (пропущено: ${skipParts.join(', ')})` : '';
    const actionWord = appendMode ? 'Дополнить' : 'Перезаписать';
    if (!confirm(`${actionWord} ${activeGroupFiles.length} активных файлов группы "${groupName}"${skipInfo}?`)) return;

    setIsSaving(true);
    setMessage('');
    setProgress({ current: 0, total: activeGroupFiles.length });

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (let i = 0; i < activeGroupFiles.length; i++) {
        const file = activeGroupFiles[i];
        try {
          let newContent = editText;
          if (appendMode) {
            const { data } = await filesAPI.getOne(file.name);
            const oldContent = data.content || '';
            newContent = oldContent.trim() ? oldContent.replace(/\n+$/, '') + '\n' + editText : editText;
          }
          await filesAPI.update(file.name, newContent);
          successCount++;
          setProgress(p => ({ ...p, current: p.current + 1 }));
          if (i < activeGroupFiles.length - 1) await new Promise(r => setTimeout(r, 300));
        } catch (error) {
          errorCount++;
          const errorMsg = error.response?.data?.error || error.message;
          errors.push({ name: file.name, error: errorMsg });
          if (error.response?.status === 401) {
            setMessage('Сессия истекла. Страница будет перезагружена...');
            setTimeout(() => window.location.reload(), 1500);
            return;
          }
        }
      }

      const skippedNote =
        (skippedGroupFiles.length > 0 ? `\n⏭ Пропущено (неактивных): ${skippedGroupFiles.length}` : '') +
        (lockedGroupFiles.length > 0 ? `\n🔒 Пропущено (под замком): ${lockedGroupFiles.length}` : '');
      if (errorCount === 0) {
        setMessage(`✅ Обновлено файлов: ${successCount}${skippedNote}`);
        setTimeout(() => onClose(), 2000);
      } else {
        setMessage(`✅ Обновлено: ${successCount}\n❌ Ошибок: ${errorCount}${skippedNote}\n${errors.map(e => `• ${e.name}: ${e.error}`).join('\n')}`);
      }
    } catch (error) {
      setMessage('Ошибка: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bulk-editor-overlay">
      <div className="bulk-editor-modal">
        <div className="bulk-editor-header">
          <h2><Tag size={16} /> Группа: {groupName}</h2>
          <button onClick={onClose} className="close-btn"><X size={15} /></button>
        </div>

        <div className="bulk-editor-info">
          <p>Текст будет записан в <strong>активные файлы группы "{groupName}"</strong> ({activeGroupFiles.length} из {groupFiles.length} шт).</p>
          {skippedGroupFiles.length > 0 && (
            <p className="warning-text" style={{ color: 'var(--text-muted)' }}>
              <AlertTriangle size={13} /> Пропущено {skippedGroupFiles.length} неактивных: {skippedGroupFiles.map(f => f.name.replace(/\.json$/, '')).join(', ')}
            </p>
          )}
          {lockedGroupFiles.length > 0 && (
            <p className="warning-text" style={{ color: 'var(--text-muted)' }}>
              <Lock size={13} /> Пропущено {lockedGroupFiles.length} под замком: {lockedGroupFiles.map(f => f.name.replace(/\.json$/, '')).join(', ')}
            </p>
          )}
          <p className="warning-text">
            <AlertTriangle size={13} /> Все активные файлы группы получат одинаковое содержимое!
          </p>
          <label className="bulk-checkbox-row">
            <input
              type="checkbox"
              checked={appendMode}
              onChange={(e) => setAppendMode(e.target.checked)}
            />
            Добавить к старым ссылкам (не заменять)
          </label>
          {isSaving && progress.total > 0 && (
            <div className="progress-section">
              <div className="progress-label">Сохранено {progress.current} из {progress.total}...</div>
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: `${(progress.current / progress.total) * 100}%` }} />
              </div>
            </div>
          )}
          {message && (
            <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}
        </div>

        {groupFiles.length === 0 ? (
          <div className="bulk-editor-loading">
            <p>В этой группе нет файлов</p>
          </div>
        ) : (
          <div className="bulk-editor-content">
            <textarea
              className="bulk-textarea"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder="Вставьте VPN-ссылки или любой текст..."
              spellCheck={false}
            />
          </div>
        )}

        <div className="bulk-editor-footer">
          <span className="file-count">Активных: {activeGroupFiles.length} / {groupFiles.length}</span>
          <div className="bulk-actions">
            <button onClick={onClose} className="btn btn-secondary" disabled={isSaving}>Отмена</button>
            <button onClick={handleSave} className="btn btn-success" disabled={isSaving || activeGroupFiles.length === 0}>
              <Save size={14} /> {isSaving ? 'Сохранение...' : (appendMode ? 'Добавить в группу' : 'Сохранить группу')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GroupBulkEditor;
